<template>
  <div class="country-config">
    <div class="operation-bar">
      <el-button type="primary" @click="addMapping"> 新增 </el-button>
      <el-input
        v-model="search.keyword"
        placeholder="搜索货币类型或货币符号"
        style="width: 300px; margin-left: 10px;"
        @keyup.enter="searchList"
        clearable
      >
        <el-button slot="append" icon="el-icon-search" @click="searchList"></el-button>
      </el-input>
    </div>

    <el-table 
      :data="tableData" 
      style="width: 100%" 
      height="480px" 
      border
      v-loading="loading"
    >
      <el-table-column prop="id" label="ID" align="center" width="80">
      </el-table-column>
      <el-table-column prop="countryCurrency" label="货币类型" align="center" width="120">
      </el-table-column>
      <el-table-column prop="currencySymbol" label="货币符号" align="center" width="120">
      </el-table-column>
      <el-table-column prop="countryCodeList" label="国家代码" align="center" min-width="200">
        <template slot-scope="scope">
          <span v-if="scope.row.countryCodeList && scope.row.countryCodeList.length > 0">
            {{ Array.isArray(scope.row.countryCodeList) ? scope.row.countryCodeList.join(', ') : scope.row.countryCodeList }}
          </span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" align="center" width="160">
        <template slot-scope="scope">
          {{ formatTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="operator" label="操作人" align="center" width="120">
      </el-table-column>
      <el-table-column label="操作" align="center" width="180" fixed="right">
        <template slot-scope="scope">
          <el-button type="primary" size="small" @click="editMapping(scope.row)"> 编辑 </el-button>
          <el-button type="danger" size="small" @click="delData(scope.row.id)"> 删除 </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container" style="margin-top: 20px; text-align: center;">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      >
      </el-pagination>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog 
      :title="isEdit ? '编辑货币国家关联' : '新增货币国家关联'" 
      :visible.sync="addVisible" 
      width="600px" 
      :append-to-body="true"
      @open="onDialogOpen"
    >
      <el-form :model="formData" :rules="formRules" ref="mappingForm" label-width="120px">
        <el-form-item label="货币类型" prop="countryCurrency">
          <el-input 
            v-model="formData.countryCurrency" 
            placeholder="请输入3位货币代码，如USD" 
            :disabled="isEdit"
            @input="handleCurrencyInput"
            style="width: 200px;"
          />
          <span class="form-tip">3位大写字母，如：USD、EUR</span>
        </el-form-item>
        
        <el-form-item label="货币符号" prop="currencySymbol">
          <el-input 
            v-model="formData.currencySymbol" 
            placeholder="请输入货币符号，如$" 
            style="width: 200px;"
          />
          <span class="form-tip">如：$、€、￥</span>
        </el-form-item>
        
        <el-form-item label="关联国家" prop="countryCodeList">
          <el-select
            v-model="formData.countryCodeList"
            multiple
            filterable
            placeholder="请选择国家"
            style="width: 100%;"
            @change="handleCountryChange"
          >
            <el-option
              v-for="country in availableCountries"
              :key="country.value"
              :label="country.label"
              :value="country.value"
            />
          </el-select>
          <div class="selected-countries" v-if="formData.countryCodeList.length > 0">
            <span class="label">已选择：</span>
            <el-tag
              v-for="code in formData.countryCodeList"
              :key="code"
              closable
              size="small"
              style="margin-right: 8px; margin-top: 5px;"
              @close="removeCountry(code)"
            >
              {{ getCountryNameByCode(code) }} ({{ code }})
            </el-tag>
          </div>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="addVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { UTCTimeToDate } from "@/utils/common";
import { 
  getEnhancedPriceList,
  getCurrencyMappingList,
  createCurrencyMapping,
  updateCurrencyMapping,
  deleteCurrencyMapping,
  getAvailableCountryList
} from "@/api/priceRequest";

export default {
  name: 'CountryConfig',
  data() {
    return {
      tableData: [],
      availableCountries: [], // 可用的国家列表（已过滤）
      addVisible: false,
      loading: false,
      submitting: false,
      isEdit: false,
      search: {
        keyword: ''
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      formData: {
        id: null,
        countryCurrency: '',
        currencySymbol: '',
        countryCodeList: []
      },
      formRules: {
        countryCurrency: [
          { required: true, message: '请输入货币类型', trigger: 'blur' },
          { pattern: /^[A-Z]{3}$/, message: '货币类型必须是3位大写字母', trigger: 'blur' }
        ],
        currencySymbol: [
          { required: true, message: '请输入货币符号', trigger: 'blur' }
        ],
        countryCodeList: [
          { type: 'array', required: true, message: '请至少选择一个国家', trigger: 'change', min: 1 }
        ]
      }
    }
  },
  methods: {
    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return '-';
      return UTCTimeToDate(timestamp, "yyyy-MM-dd HH:mm:ss");
    },
    
    // 根据国家代码获取国家名称
    getCountryNameByCode(code) {
      // 先从可用国家列表中查找
      const country = this.availableCountries.find(item => item.value === code);
      if (country) {
        return country.label || code;
      }
      return code;
    },
    
    // 处理货币类型输入
    handleCurrencyInput(value) {
      this.formData.countryCurrency = value.toUpperCase();
    },
    
    // 处理国家选择变化
    handleCountryChange(selectedCodes) {
      console.log('Selected countries:', selectedCodes);
    },
    
    // 移除国家
    removeCountry(countryCode) {
      this.formData.countryCodeList = this.formData.countryCodeList.filter(code => code !== countryCode);
    },
    
    // 搜索
    searchList() {
      this.pagination.page = 1;
      this.getList();
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.page = 1;
      this.getList();
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.page = val;
      this.getList();
    },
    
    // 弹窗打开时
    async onDialogOpen() {
      await this.loadAvailableCountries();
    },
    
    // 加载可用的国家列表
    async loadAvailableCountries() {
      try {
        // 传入当前编辑记录的ID（如果是编辑模式）
        const id = this.isEdit ? this.formData.id : null;
        const res = await getAvailableCountryList('available',id);
        
        if (res.code === 0) {
          // 根据接口返回的数据格式进行适配
          if (res.data && res.data.list) {
            // 如果返回的是标准格式的国家列表
            this.availableCountries = res.data.list.map(code => ({
              value: code,
              label: code
            }));
          } else if (Array.isArray(res.data)) {
            // 如果直接返回国家代码数组
            this.availableCountries = res.data.map(code => ({
              value: code,
              label: code
            }));
          }
        } else {
          this.$message.error(res.msg || '获取可用国家列表失败');
        }
      } catch (error) {
        console.error('获取可用国家列表失败:', error);
        this.$message.error('获取可用国家列表失败');
      }
    },
    
    // 重置表单
    resetForm() {
      this.formData = {
        id: null,
        countryCurrency: '',
        currencySymbol: '',
        countryCodeList: []
      };
      if (this.$refs.mappingForm) {
        this.$refs.mappingForm.resetFields();
      }
    },
    
    // 新增
    async addMapping() {
      this.isEdit = false;
      this.addVisible = true;
      this.resetForm();
    },
    
    // 编辑
    editMapping(row) {
      this.isEdit = true;
      this.addVisible = true;
      this.formData = {
        id: row.id,
        countryCurrency: row.countryCurrency,
        currencySymbol: row.currencySymbol,
        countryCodeList: [...(row.countryCodeList || [])]
      };
    },
    
    // 提交表单
    async submitForm() {
      try {
        await this.$refs.mappingForm.validate();
        
        this.submitting = true;
        
        const apiMethod = this.isEdit ? updateCurrencyMapping : createCurrencyMapping;
        const res = await apiMethod(this.formData);
        
        if (res.code === 0) {
          this.$message.success(this.isEdit ? '编辑成功' : '创建成功');
          this.addVisible = false;
          this.getList();
          // 通知父组件刷新价格列表
          this.$parent.query && this.$parent.query();
        } else {
          this.$message.error(res.msg || '操作失败');
        }
      } catch (error) {
        if (error !== false) { // 排除表单验证失败的情况
          console.error('提交失败:', error);
          this.$message.error('操作失败');
        }
      } finally {
        this.submitting = false;
      }
    },
    
    // 获取列表数据 - 使用currency_country_mapping接口
    async getList() {
      this.loading = true;
      try {
        const res = await getCurrencyMappingList({
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          keyword: this.search.keyword
        });
        
        if (res.code === 0) {
          this.tableData = res.data.list || [];
          this.pagination.total = res.data.total || 0;
        } else {
          this.$message.error(res.msg || '获取数据失败');
        }
      } catch (error) {
        console.error('获取列表失败:', error);
        this.$message.error('获取数据失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 删除
    async delData(id) {
      this.$confirm('确认删除该货币国家关联？删除后相关的价格配置可能受到影响。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteCurrencyMapping(id);
          
          if (res.code === 0) {
            this.$message.success('删除成功');
            this.getList();
            // 通知父组件刷新价格列表
            this.$parent.query && this.$parent.query();
          } else {
            this.$message.error(res.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      }).catch(() => {
        // 用户取消删除
      });
    }
  },
  async mounted() {
    this.getList();
  }
}
</script>

<style scoped lang="scss">
.country-config {
  .operation-bar {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
  }
  
  .pagination-container {
    border-top: 1px solid #ebeef5;
    padding-top: 20px;
  }
  
  .form-tip {
    margin-left: 10px;
    color: #909399;
    font-size: 12px;
  }
  
  .selected-countries {
    margin-top: 10px;
    padding: 10px;
    background: #f5f7fa;
    border-radius: 4px;
    
    .label {
      color: #606266;
      font-size: 12px;
      margin-right: 8px;
    }
  }
}
</style>
